// src/pages/Consultation/ConsultationDashboard.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FiPlus,
  FiClock,
  FiUsers,
  FiDollarSign,
  FiTrendingUp,
  FiFilter,
  FiRefreshCw,
  FiGrid,
  FiList,
  FiAlertCircle,
} from "react-icons/fi";
import {
  getAdminActiveSessionsThunk,
  getBranchSessionsThunk,
} from "../../store/thunks/consultationThunks";
import { setFilters, resetFilters } from "../../store/slices/consultationSlice";
import Button from "../../components/common/Button";
import SessionCard from "./components/SessionCard";
import SessionStats from "./components/SessionStats";
import StartSessionModal from "./StartSessionModal";
import toast from "react-hot-toast";

const ConsultationDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state
  const { sessions, activeSessions, loading, pagination, filters } =
    useSelector((state) => state.consultation);

  const { user } = useSelector((state) => state.auth);
  const { branches } = useSelector((state) => state.branch);
  const { selectedBranch } = useSelector((state) => state.rate.ui);

  // Local state
  const [viewMode, setViewMode] = useState("grid");
  const [showFilters, setShowFilters] = useState(false);
  const [showStartModal, setShowStartModal] = useState(false);
  const [activeView, setActiveView] = useState("my-sessions"); // 'my-sessions' | 'branch-sessions' | 'all-sessions'

  // Load sessions based on view
  const loadSessions = useCallback(() => {
    const adminCode = user?.adminCode || user?._id;

    if (activeView === "my-sessions") {
      dispatch(getAdminActiveSessionsThunk(adminCode));
    } else if (activeView === "branch-sessions") {
      const branchCode = selectedBranch || user?.branchAccess?.primaryBranch;
      dispatch(
        getBranchSessionsThunk({
          branchCode,
          filters: {
            ...filters,
            page: filters.page || 1,
            limit: filters.limit || 20,
          },
        })
      );
    }
  }, [dispatch, activeView, user, selectedBranch, filters]);

  // Initial load
  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadSessions();
    }, 30000);

    return () => clearInterval(interval);
  }, [loadSessions]);

  // Filter handlers
  const handleFilterChange = (field, value) => {
    dispatch(setFilters({ ...filters, [field]: value, page: 1 }));
  };

  const handleClearFilters = () => {
    dispatch(resetFilters());
  };

  // View handlers
  const handleViewSession = (sessionId) => {
    navigate(`/consultation/sessions/${sessionId}`);
  };

  // Calculate stats from sessions
  const calculateStats = () => {
    const sessionsToUse =
      activeView === "my-sessions" ? activeSessions : sessions;

    console.log(">>sessionsToUse", sessionsToUse);

    const totalActive = sessionsToUse.filter(
      (s) => s.status === "ACTIVE"
    ).length;
    const totalPaused = sessionsToUse.filter(
      (s) => s.status === "PAUSED"
    ).length;
    const totalCartValue = sessionsToUse.reduce((sum, s) => {
      return sum + (s.initialCartSnapshot?.totalValue || 0);
    }, 0);

    const avgDuration =
      sessionsToUse.length > 0
        ? sessionsToUse.reduce((sum, s) => sum + (s.duration || 0), 0) /
          sessionsToUse.length
        : 0;

    return {
      totalActive,
      totalPaused,
      totalSessions: sessionsToUse.length,
      totalCartValue,
      avgDuration: Math.round(avgDuration),
    };
  };

  const stats = calculateStats();
  const displaySessions =
    activeView === "my-sessions" ? activeSessions : sessions;

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Consultation Sessions
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage customer consultation sessions
          </p>
        </div>

        {/* {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start justify-between">
            <div className="flex items-start gap-3">
              <FiAlertCircle className="text-red-600 text-xl mt-0.5" />
              <div>
                <p className="font-medium text-red-900">Error</p>
                <p className="text-sm text-red-800 mt-1">{error}</p>
              </div>
            </div>
            <button
              onClick={() => dispatch(clearSessionError())}
              className="text-red-600 hover:text-red-800"
            >
              <FiX size={20} />
            </button>
          </div>
        )} */}

        <div className="flex gap-3">
          <Button variant="outline" onClick={loadSessions} disabled={loading}>
            <FiRefreshCw className={`mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>

          <Button onClick={() => navigate("/consultation/start-session")}>
            <FiPlus className="mr-2" />
            Start Session
          </Button>
        </div>
      </div>

      {/* View Tabs */}
      <div className="mb-6">
        <div className="flex gap-2 border-b border-gray-200">
          <button
            onClick={() => setActiveView("my-sessions")}
            className={`px-4 py-2 font-medium ${
              activeView === "my-sessions"
                ? "text-primary border-b-2 border-primary"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            My Active Sessions
            {activeSessions.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-primary text-white rounded-full text-xs">
                {activeSessions.length}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveView("branch-sessions")}
            className={`px-4 py-2 font-medium ${
              activeView === "branch-sessions"
                ? "text-primary border-b-2 border-primary"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Branch Sessions
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <SessionStats stats={stats} />

      {/* Filters Bar */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <input
              type="text"
              placeholder="Search by session code, customer..."
              value={filters.search || ""}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary w-64"
            />

            <select
              value={filters.status || ""}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="PAUSED">Paused</option>
              <option value="COMPLETED">Completed</option>
              <option value="CONVERTED">Converted</option>
            </select>

            <select
              value={filters.sessionType || ""}
              onChange={(e) =>
                handleFilterChange("sessionType", e.target.value)
              }
              className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Types</option>
              <option value="APPOINTMENT">Appointment</option>
              <option value="WALK_IN">Walk-in</option>
            </select>

            {Object.values(filters).some((v) => v) && (
              <Button variant="outline" size="sm" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                className={`p-1.5 rounded transition-all ${
                  viewMode === "grid" ? "bg-white shadow" : ""
                }`}
                onClick={() => setViewMode("grid")}
              >
                <FiGrid />
              </button>
              <button
                className={`p-1.5 rounded transition-all ${
                  viewMode === "list" ? "bg-white shadow" : ""
                }`}
                onClick={() => setViewMode("list")}
              >
                <FiList />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Sessions Display */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : displaySessions.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <FiAlertCircle className="mx-auto text-5xl text-gray-400 mb-4" />
          <p className="text-gray-500 mb-4">
            {activeView === "my-sessions"
              ? "You have no active sessions"
              : "No sessions found"}
          </p>
          <Button onClick={() => navigate("/consultation/start-session")}>
            <FiPlus className="mr-2" />
            Create Order Session
          </Button>
        </div>
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {displaySessions.map((session) => (
            <SessionCard
              key={session._id}
              session={session}
              onView={handleViewSession}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Session
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Admin
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cart Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {displaySessions.map((session) => (
                <tr key={session._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {session.sessionCode}
                    </div>
                    <div className="text-sm text-gray-500">
                      {session.sessionType}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {session.userId?.name ||
                        session.guestCustomer?.name ||
                        "Guest"}
                    </div>
                    <div className="text-sm text-gray-500">
                      {session.userId?.phoneNumber ||
                        session.guestCustomer?.phoneNumber ||
                        "-"}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {session.primaryAdmin?.adminName}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {Math.floor((session.duration || 0) / 60)}h{" "}
                      {(session.duration || 0) % 60}m
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-primary">
                      ₹
                      {(
                        session.initialCartSnapshot?.totalValue || 0
                      ).toLocaleString("en-IN")}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        session.status === "ACTIVE"
                          ? "bg-green-100 text-green-800"
                          : session.status === "PAUSED"
                          ? "bg-yellow-100 text-yellow-800"
                          : session.status === "COMPLETED"
                          ? "bg-blue-100 text-blue-800"
                          : session.status === "CONVERTED"
                          ? "bg-purple-100 text-purple-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {session.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleViewSession(session._id)}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex justify-center items-center gap-4 mt-6">
          <Button
            variant="outline"
            disabled={filters.page === 1}
            onClick={() => handleFilterChange("page", filters.page - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-gray-600">
            Page {filters.page} of {pagination.pages}
          </span>
          <Button
            variant="outline"
            disabled={filters.page === pagination.pages}
            onClick={() => handleFilterChange("page", filters.page + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Start Session Modal */}
      {showStartModal && (
        <StartSessionModal
          onClose={() => setShowStartModal(false)}
          onSuccess={(session) => {
            setShowStartModal(false);
            navigate(`/consultation/sessions/${session._id}`);
            toast.success("Session started successfully");
          }}
        />
      )}
    </div>
  );
};

export default ConsultationDashboard;
